import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { MaskedLines, EASE } from "./Reveal";
import { scrollToId } from "../lib/scroll";

/* The scene scales with its box; measure it so typography can too. */
function useSceneWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => setW(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

const LINE_COLOR = "#7C63A8";
const PURPLE_INK = "#7C63A8";

/*
 * Scene geometry lives in a 1400 x 1000 landscape design space (the SVG viewBox).
 * Positions are percentages of that box so everything scales together.
 */
const ORBS = [
  { id: "mango", src: "/images/product/orb-mango.webp", cx: 390, cy: 190, r: 115, range: [0.25, 0.34], dur: 7.5, drift: -30 },
  { id: "jarorb", src: "/images/product/orb-jar.webp", cx: 480, cy: 420, r: 120, range: [0.31, 0.4], dur: 8.5, drift: 34 },
  { id: "oats", src: "/images/product/orb-oats.webp", cx: 920, cy: 400, r: 120, range: [0.37, 0.46], dur: 7, drift: -26 },
  { id: "honey", src: "/images/product/orb-honey.webp", cx: 400, cy: 660, r: 125, range: [0.49, 0.58], dur: 9, drift: 30 },
  { id: "apricot", src: "/images/product/orb-apricot.webp", cx: 935, cy: 620, r: 100, range: [0.55, 0.64], dur: 7.8, drift: -24 },
];

const CALLOUTS = [
  {
    id: "shea", side: "left", title: "Shea Butter", desc: "Softens dry, rough skin.",
    x: 3, w: 18, top: 12, d: "M 300 145 H 340 L 385 190", dot: [388, 193], range: [0.28, 0.38],
  },
  {
    id: "ceramides", side: "right", title: "Ceramides + Cholesterol + Phytosphingosine", desc: "Support the moisture barrier.",
    x: 79, w: 18, top: 12, d: "M 1100 145 H 1050 L 920 300", dot: [917, 303], range: [0.34, 0.44],
  },
  {
    id: "collagen", side: "left", title: "Hydrolyzed Collagen", desc: "Helps bind moisture for a supple feel.",
    x: 3, w: 18, top: 32, d: "M 300 330 H 345 L 375 405", dot: [378, 408], range: [0.4, 0.5],
  },
  {
    id: "oatmeal", side: "right", title: "Colloidal Oatmeal + Panthenol", desc: "Comfort dry-feeling skin.",
    x: 79, w: 18, top: 32, d: "M 1100 345 H 1075 L 1048 400", dot: [1045, 403], range: [0.46, 0.56],
  },
  {
    id: "squalane", side: "left", title: "Squalane + Glycerin + Hyaluronic Acid", desc: "Attract and retain moisture.",
    x: 3, w: 18, top: 52, d: "M 300 530 H 340 L 425 585", dot: [428, 588], range: [0.52, 0.62],
  },
  {
    id: "peptides", side: "right", title: "Peptides", desc: "Support smoother-looking skin.",
    x: 79, w: 18, top: 52, d: "M 1100 545 H 1075 L 1040 615", dot: [1037, 618], range: [0.58, 0.68],
  },
  {
    id: "honey", side: "left", title: "Manuka Honey", desc: "Hydrates and conditions.",
    x: 3, w: 18, top: 72, d: "M 300 730 H 340 L 398 687", dot: [401, 684], range: [0.64, 0.74],
  },
  {
    id: "vitamin-e", side: "right", title: "Vitamin E", desc: "Antioxidant care + skin conditioning.",
    x: 79, w: 18, top: 72, d: "M 1100 735 H 1050 L 1025 705", dot: [1022, 702], range: [0.7, 0.8],
  },
];

function CalloutLine({ c, progress }) {
  const [a, b] = c.range;
  const pathLength = useTransform(progress, [a, b], [0, 1]);
  const lineO = useTransform(progress, [a, a + 0.012], [0, 1]);
  const dotScale = useTransform(progress, [b, b + 0.045], [0, 1]);
  return (
    <g aria-hidden>
      <motion.path
        d={c.d}
        fill="none"
        stroke={LINE_COLOR}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        style={{ pathLength, opacity: lineO }}
      />
      <motion.circle
        cx={c.dot[0]}
        cy={c.dot[1]}
        r="5"
        fill={LINE_COLOR}
        style={{ scale: dotScale, transformBox: "fill-box", transformOrigin: "center" }}
      />
    </g>
  );
}

function CalloutLabel({ c, progress, scale }) {
  const [a, b] = c.range;
  const opacity = useTransform(progress, [b - 0.015, b + 0.05], [0, 1]);
  const x = useTransform(progress, [b - 0.015, b + 0.05], [c.side === "left" ? -18 : 18, 0]);
  return (
    <motion.div
      data-testid={`callout-${c.id}`}
      style={{
        left: `${c.x}%`,
        top: `${c.top}%`,
        width: `${c.w}%`,
        opacity,
        x,
        fontSize: scale ? Math.max(scale * 0.023, 12) : 14,
      }}
      className="absolute"
    >
      <p
        className="font-display font-semibold leading-[1.14]"
        style={{ color: c.side === "right" ? PURPLE_INK : "#171310" }}
      >
        {c.title}
      </p>
      <p
        className="font-sans font-light text-stone leading-[1.36]"
        style={{ fontSize: scale ? Math.max(scale * 0.0135, 9) : 10, marginTop: scale ? scale * 0.005 : 3 }}
      >
        {c.desc}
      </p>
    </motion.div>
  );
}

function Orb({ o, progress }) {
  const [a, b] = o.range;
  const opacity = useTransform(progress, [a, a + 0.05], [0, 1]);
  const scale = useTransform(progress, [a, b + 0.04], [0.3, 1]);
  const y = useTransform(progress, [a, b + 0.04], [o.drift, 0]);
  const rotate = useTransform(progress, [a, b + 0.04], [o.drift > 0 ? -14 : 14, 0]);
  return (
    <motion.div
      style={{
        left: `${((o.cx - o.r) / 1400) * 100}%`,
        top: `${((o.cy - o.r) / 1000) * 100}%`,
        width: `${((o.r * 2) / 1400) * 100}%`,
        opacity,
        scale,
        y,
        rotate,
      }}
      className="absolute"
      data-testid={`scene-orb-${o.id}`}
    >
      <motion.img
        src={o.src}
        alt=""
        draggable={false}
        animate={{ y: [0, -12, 0] }}
        transition={{ duration: o.dur, repeat: Infinity, ease: "easeInOut", delay: -o.dur / 3 }}
        className="w-full h-auto select-none"
      />
    </motion.div>
  );
}

function GlassBubble({ x, y, r, delay = 0, drift = -18 }) {
  return (
    <motion.div
      aria-hidden
      style={{ left: `${((x - r) / 1400) * 100}%`, top: `${((y - r) / 1000) * 100}%`, width: `${((r * 2) / 1400) * 100}%` }}
      className="absolute"
    >
      <motion.div
        animate={{ y: [0, drift, 0] }}
        transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut", delay }}
        className="w-full aspect-full rounded-full bg-[radial-gradient(circle_at_32%_28%,rgba(255,255,255,0.98),rgba(214,205,235,0.55)_40%,rgba(160,140,191,0.28)_68%,rgba(160,140,191,0.10))] shadow-[inset_0_-8px_18px_rgba(110,91,142,0.30),inset_0_4px_10px_rgba(255,255,255,0.8),0_12px_26px_rgba(110,91,142,0.16)] ring-1 ring-white/60"
      >
        <div className="w-[22%] h-[22%] rounded-full bg-white/90 blur-[1px] ml-[16%] mt-[14%]" />
      </motion.div>
    </motion.div>
  );
}

function LiquidDrop({ x, y, s, amber = false, delay = 0, drift = -12 }) {
  return (
    <motion.div
      aria-hidden
      style={{ left: `${((x - s) / 1400) * 100}%`, top: `${((y - s) / 1000) * 100}%`, width: `${((s * 2) / 1400) * 100}%` }}
      className="absolute"
    >
      <motion.div
        animate={{ y: [0, drift, 0], rotate: 45 }}
        transition={{ duration: 6.2, repeat: Infinity, ease: "easeInOut", delay }}
        className={`aspect-square shadow-[0_8px_18px_rgba(110,91,142,0.18)] ${amber
          ? "bg-[radial-gradient(circle_at_32%_28%,#FFE9A8,#F0B94E_55%,#C08312)]"
          : "bg-[radial-gradient(circle_at_32%_28%,rgba(255,255,255,0.96),rgba(206,197,233,0.55)_48%,rgba(160,140,191,0.28))]"}`}
        style={{ borderRadius: amber ? "10% 62% 58% 58%" : "12% 64% 58% 58%" }}
      />
    </motion.div>
  );
}

function AmberDrop({ x, y, r }) {
  return (
    <motion.div
      aria-hidden
      style={{ left: `${((x - r) / 1400) * 100}%`, top: `${((y - r) / 1000) * 100}%`, width: `${((r * 2) / 1400) * 100}%` }}
      className="absolute"
    >
      <motion.div
        animate={{ y: [0, -14, 0] }}
        transition={{ duration: 7.4, repeat: Infinity, ease: "easeInOut" }}
        className="w-full aspect-full rounded-full bg-[radial-gradient(circle_at_34%_30%,#FFE9A8,#F6C453_46%,#DE9A1E_72%,#B87708)] shadow-[inset_0_-8px_18px_rgba(146,94,4,0.35),0_12px_26px_rgba(184,119,8,0.25)]"
      >
        <div className="w-[20%] h-[20%] rounded-full bg-white/85 blur-[1.5px] ml-[18%] mt-[15%]" />
      </motion.div>
    </motion.div>
  );
}

function Headline({ className = "" }) {
  return (
    <div className={`text-center ${className}`} data-testid="layered-headline">
      <MaskedLines
        lines={["Vivoré Luxe Hydrating Cream"]}
        className="eyebrow !tracking-widest2 !text-[11px] md:!text-xs text-ink/50 uppercase"
        lineClassName="font-sans"
      />
      <MaskedLines
        lines={["Hydration, beautifully layered."]}
        delay={0.1}
        className="mt-3 font-display font-medium text-3xl md:text-5xl text-ink"
      />
    </div>
  );
}

function ScentLine({ style, className = "" }) {
  return (
    <motion.div
      style={style}
      data-testid="layered-scent"
      className={`flex items-center gap-5 md:gap-7 ${className}`}
    >
      <span className="h-px flex-1 bg-ink/15" />
      <p className="font-display italic text-sm md:text-base text-ink/70 text-center">
        Lightly scented with mango, peach &amp; apricot.
      </p>
      <span className="h-px flex-1 bg-ink/15" />
    </motion.div>
  );
}

/* ---- the scene, in the 1400x1000 design space ---- */

function Scene({ progress }) {
  const jarO = useTransform(progress, [0.02, 0.1], [0, 1]);
  const jarY = useTransform(progress, [0.02, 0.32], [150, 0]);
  const jarS = useTransform(progress, [0.02, 0.32], [0.9, 1]);
  const shadowW = useTransform(progress, [0.02, 0.32], ["58%", "100%"]);
  const shadowO = useTransform(progress, [0.02, 0.32], [0, 0.22]);
  const cloudO = useTransform(progress, [0.16, 0.28], [0, 1]);
  const cloudS = useTransform(progress, [0.16, 0.44], [0.55, 1]);
  const cloudY = useTransform(progress, [0.16, 0.44], [90, 0]);
  const swirl = useTransform(progress, [0.18, 0.85], [-2.5, 2.5]);

  return (
    <div className="relative h-full w-full">
      {/* glows */}
      <motion.div
        aria-hidden
        style={{ opacity: cloudO }}
        className="absolute left-[20%] top-[-12%] w-[60%] aspect-square rounded-full bg-[radial-gradient(closest-side,rgba(160,140,191,0.30),rgba(246,243,238,0)_72%)]"
      />
      <motion.div
        aria-hidden
        style={{ opacity: shadowO }}
        className="absolute left-1/2 -translate-x-1/2 bottom-[2.5%] h-[4.5%] rounded-[50%] bg-ink blur-xl"
        animate={{ width: shadowW }}
      />

      {/* jar — the anchor */}
      <motion.img
        data-testid="scene-jar"
        src="/images/product/jar-tight.webp"
        alt="Vivoré Luxe Hydrating Cream jar"
        draggable={false}
        style={{ opacity: jarO, y: jarY, scale: jarS, x: "-50%" }}
        className="absolute left-1/2 bottom-[2.5%] w-[42%] select-none"
      />

      {/* whipped cream cloud */}
      <motion.div style={{ opacity: cloudO, scale: cloudS, y: cloudY }} className="absolute left-[35%] top-[6%] w-[30%]">
        <motion.img
          src="/images/product/cream-cloud.webp"
          alt=""
          draggable={false}
          animate={{ scale: [1, 1.035, 1], rotate: [0, 1.6, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="w-full h-auto select-none [filter:drop-shadow(0_30px_50px_rgba(110,91,142,0.30))]"
        />
      </motion.div>

      {/* ingredient constellation */}
      <motion.div style={{ rotate: swirl }} className="absolute inset-0">
        {ORBS.map((o) => (
          <Orb key={o.id} o={o} progress={progress} />
        ))}
        <GlassBubble x={430} y={590} r={40} />
        <GlassBubble x={760} y={130} r={28} delay={1.2} drift={-24} />
        <GlassBubble x={835} y={585} r={24} delay={2.1} drift={-14} />
        <AmberDrop x={960} y={690} r={60} />

        {/* liquid spray + droplets */}
        <LiquidDrop x={645} y={125} s={17} delay={0.4} />
        <LiquidDrop x={705} y={92} s={13} delay={1.1} />
        <LiquidDrop x={765} y={128} s={15} delay={1.8} />
        <LiquidDrop x={592} y={158} s={11} delay={2.4} />
        <LiquidDrop x={852} y={520} s={21} delay={0.2} />
        <LiquidDrop x={548} y={520} s={15} delay={1.5} />
        <LiquidDrop x={540} y={350} s={14} amber delay={2} />
        <LiquidDrop x={878} y={330} s={13} amber delay={0.9} />
      </motion.div>
    </div>
  );
}

function SceneCta({ progress }) {
  const opacity = useTransform(progress, [0.9, 0.965], [0, 1]);
  const y = useTransform(progress, [0.9, 0.965], [24, 0]);
  return (
    <motion.div style={{ opacity, y }} className="flex flex-col items-center gap-2.5">
      <Link
        to="/products/luxe-hydrating-cream"
        data-testid="layered-cta"
        className="btn-lux bg-ink text-ivory text-xs uppercase tracking-widest2 px-10 py-4 rounded-full flex items-center gap-2.5"
      >
        View Full Details <ArrowUpRight size={14} strokeWidth={1.5} />
      </Link>
      <motion.button
        data-testid="layered-scroll-cue"
        onClick={() => scrollToId("shop")}
        animate={{ y: [0, 7, 0], opacity: [0.55, 1, 0.55] }}
        transition={{ duration: 1.9, repeat: Infinity, ease: "easeInOut" }}
        className="p-1.5 text-ink/60 hover:text-ink transition-colors duration-500"
        aria-label="Scroll down to the product"
      >
        <ChevronDown size={19} strokeWidth={1.5} />
      </motion.button>
    </motion.div>
  );
}

export default function LayeredHydration() {
  const ref = useRef(null);
  const [sceneRef, sceneW] = useSceneWidth();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.4", "end end"],
  });

  const scentO = useTransform(scrollYProgress, [0.88, 0.96], [0, 1]);
  const scentY = useTransform(scrollYProgress, [0.88, 0.96], [20, 0]);

  return (
    <>
      {/* Desktop — pinned scene scrubbed to scroll */}
      <section
        ref={ref}
        data-testid="layered-section"
        className="relative hidden lg:block h-[300vh] bg-gradient-to-b from-[#F5EDF1] via-[#F8F4F1] to-[#F6ECEA]"
      >
        <div className="sticky top-0 h-screen flex flex-col overflow-hidden">
          <Headline className="pt-14 px-6" />

          <div className="relative flex-1 min-h-0 w-full flex items-center justify-center px-8">
            <div ref={sceneRef} className="relative h-full max-h-[calc(100vh-185px)] aspect-[1400/1000]">
              <Scene progress={scrollYProgress} />
              <svg
                aria-hidden
                className="absolute inset-0 w-full h-full pointer-events-none"
                viewBox="0 0 1400 1000"
                fill="none"
                preserveAspectRatio="xMidYMid meet"
              >
                {CALLOUTS.map((c) => (
                  <CalloutLine key={c.id} c={c} progress={scrollYProgress} />
                ))}
              </svg>
              {CALLOUTS.map((c) => (
                <CalloutLabel key={c.id} c={c} progress={scrollYProgress} scale={sceneW} />
              ))}
            </div>
          </div>

          <div className="px-12 pb-6">
            <ScentLine style={{ opacity: scentO, y: scentY }} className="max-w-[820px] mx-auto" />
            <div className="mt-3">
              <SceneCta progress={scrollYProgress} />
            </div>
          </div>
        </div>
      </section>

      {/* Mobile / tablet — flowing collage */}
      <section
        data-testid="layered-section-mobile"
        className="lg:hidden relative py-16 px-6 bg-gradient-to-b from-[#F5EDF1] via-[#F8F4F1] to-[#F6ECEA] overflow-hidden"
      >
        <Headline />
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="relative mx-auto mt-6 max-w-[430px] aspect-[4/5]"
        >
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 26 },
              show: { opacity: 1, y: 0, transition: { duration: 1.1, ease: EASE, staggerChildren: 0.12 } },
            }}
            className="absolute inset-0"
          >
            <motion.img
              src="/images/product/orb-mango.webp"
              alt="Fresh mango"
              draggable={false}
              variants={{ hidden: { opacity: 0, scale: 0.5 }, show: { opacity: 1, scale: 1, transition: { duration: 0.9, ease: EASE } } }}
              className="absolute left-[-4%] top-[2%] w-[42%] select-none"
            />
            <motion.img
              src="/images/product/orb-oats.webp"
              alt="Colloidal oats"
              draggable={false}
              variants={{ hidden: { opacity: 0, scale: 0.5 }, show: { opacity: 1, scale: 1, transition: { duration: 0.9, ease: EASE } } }}
              className="absolute right-[-5%] top-[16%] w-[44%] select-none"
            />
            <motion.img
              src="/images/product/cream-cloud.webp"
              alt="Whipped cream texture"
              draggable={false}
              variants={{ hidden: { opacity: 0, scale: 0.6 }, show: { opacity: 1, scale: 1, transition: { duration: 1, ease: EASE } } }}
              className="absolute left-[16%] top-[26%] w-[68%] select-none"
            />
            <motion.img
              src="/images/product/orb-honey.webp"
              alt="Manuka honey"
              draggable={false}
              variants={{ hidden: { opacity: 0, scale: 0.5 }, show: { opacity: 1, scale: 1, transition: { duration: 0.9, ease: EASE } } }}
              className="absolute left-[-2%] top-[46%] w-[40%] select-none"
            />
            <motion.img
              src="/images/product/jar-tight.webp"
              alt="Vivoré Luxe Hydrating Cream jar"
              draggable={false}
              variants={{ hidden: { opacity: 0, y: 50, x: "-50%" }, show: { opacity: 1, y: 0, x: "-50%", transition: { duration: 1.1, ease: EASE } } }}
              className="absolute left-1/2 bottom-[2%] w-[78%] select-none"
            />
            <motion.img
              src="/images/product/orb-apricot.webp"
              alt="Apricot"
              draggable={false}
              variants={{ hidden: { opacity: 0, scale: 0.5 }, show: { opacity: 1, scale: 1, transition: { duration: 0.9, ease: EASE } } }}
              className="absolute right-[2%] top-[54%] w-[34%] select-none"
            />
          </motion.div>
        </motion.div>
        <div className="mx-auto mt-10 max-w-[540px] grid grid-cols-2 gap-x-7 gap-y-7">
          {CALLOUTS.map((c, i) => (
            <motion.div
              key={c.id}
              data-testid={`callout-${c.id}`}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.8, delay: (i % 2) * 0.08, ease: EASE }}
            >
              <p
                className="font-display font-semibold text-[15.5px] leading-[1.2]"
                style={{ color: c.side === "right" ? PURPLE_INK : "#171310" }}
              >
                {c.title}
              </p>
              <p className="font-sans font-light text-[12px] text-stone mt-1 leading-snug">
                {c.desc}
              </p>
            </motion.div>
          ))}
        </div>
        <div className="flex justify-center mt-10">
          <Link
            to="/products/luxe-hydrating-cream"
            data-testid="layered-cta-mobile"
            className="btn-lux bg-ink text-ivory text-xs uppercase tracking-widest2 px-10 py-4 rounded-full flex items-center gap-2.5"
          >
            View Full Details <ArrowUpRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
        <ScentLine className="mt-10 max-w-[520px] mx-auto" />
      </section>
    </>
  );
}
